import React, { useState, useMemo, useEffect } from 'react';
import { CreditCard, Plus, Trash2, DollarSign, ChevronUp, AlertCircle, Package, Check, X } from 'lucide-react';
import { useProfile } from '../ProfileContext';
import SectionWrapper from '../components/SectionWrapper';
import { supabase } from '../../../lib/supabase';
import toast from 'react-hot-toast';

const CreditCardsSection = () => {
  const {
    creditCards,
    subscriptions,
    bundles, // user_bundles
    refData,
    expandedSections,
    saving,
    addCreditCard,
    saveCreditCard,
    deleteCreditCard,
    saveAllocation,
    saveBundleAllocation,
    toggleSection,
    isSectionDirty,
    getCardAllocations,
    getServicesForCreditAllocation,
    loadProfile, // Add this to reload data after saving
  } = useProfile();

  const isExpanded = expandedSections.includes('creditCards');
  const isDirty = isSectionDirty('creditCards');

  const [showAddForm, setShowAddForm] = useState(false);
  const [expandedCardId, setExpandedCardId] = useState(null);
  const [newCard, setNewCard] = useState({
    card_name: '',
    issuer: '',
    streaming_credit_monthly: 0,
    isOther: false,
  });
  
  // Track LOCAL allocation changes (not saved until confirm)
  const [localAllocations, setLocalAllocations] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingCloseCardId, setPendingCloseCardId] = useState(null);

  // Get bundles that can be allocated to this card
  // Get bundles that are either assigned to this card OR available for allocation
  const getBundlesForCard = (cardName) => {
    return (bundles || []).filter(bundle => {
      // Show bundles already assigned to this card
      if (bundle.payment_method === 'credit_card_perk' && bundle.payment_source === cardName) {
        return true;
      }
      // Also show bundles that are 'paid' (can be allocated to any card)
      if (bundle.payment_method === 'paid' || !bundle.payment_method) {
        return true;
      }
      // Show bundles with no payment source yet
      if (!bundle.payment_source) {
        return true;
      }
      return false;
    });
  };

  // Get ALL bundles for allocation (regardless of current payment method)
  const allBundlesForAllocation = bundles || [];

  const servicesForAllocation = getServicesForCreditAllocation();

  // Reset local allocations when card panel closes
  useEffect(() => {
    if (!expandedCardId) {
      setLocalAllocations({});
      setHasUnsavedChanges(false);
    }
  }, [expandedCardId]);

  // Initialize local allocations when panel opens
  useEffect(() => {
    if (expandedCardId) {
      const card = creditCards.find(c => c.id === expandedCardId);
      if (card) {
        const initialAllocations = {};
        
        // Get current bundle allocations
        const cardBundles = getBundlesForCard(card.card_name);
        cardBundles.forEach(bundle => {
          initialAllocations[`bundle-${bundle.id}`] = parseFloat(bundle.credit_applied) || 0;
        });
        
        // Get current service allocations
        const allocations = getCardAllocations(card.id);
        allocations.forEach(alloc => {
          initialAllocations[`service-${alloc.subscription_id}`] = parseFloat(alloc.amount_allocated) || 0;
        });
        
        // Also set defaults for services without allocations
        servicesForAllocation.forEach(sub => {
          if (initialAllocations[`service-${sub.id}`] === undefined) {
            initialAllocations[`service-${sub.id}`] = 0;
          }
        });
        
        setLocalAllocations(initialAllocations);
        setHasUnsavedChanges(false);
      }
    }
  }, [expandedCardId, creditCards, bundles]);

  // Get card options from reference data
  const cardOptions = useMemo(() => {
    const cardMap = new Map();

    refData.cardPerks?.forEach(perk => {
      if (!perk.card_name) return;

      if (!cardMap.has(perk.card_name)) {
        cardMap.set(perk.card_name, {
          name: perk.card_name,
          issuer: perk.issuer || '',
          creditAmount: 0,
        });
      }

      const card = cardMap.get(perk.card_name);

      if (perk.perk_type === 'streaming_credit' && perk.credit_amount) {
        card.creditAmount = Math.max(card.creditAmount, perk.credit_amount);
      }
    });

    if (cardMap.size > 0) {
      return Array.from(cardMap.values()).sort((a, b) => {
        if (b.creditAmount !== a.creditAmount) return b.creditAmount - a.creditAmount;
        return a.name.localeCompare(b.name);
      });
    }

    // Fallback
    return [
      { name: 'American Express Platinum', issuer: 'American Express', creditAmount: 25 },
      { name: 'American Express Gold', issuer: 'American Express', creditAmount: 10 },
      { name: 'Chase Sapphire Reserve', issuer: 'Chase', creditAmount: 0 },
      { name: 'Chase Sapphire Preferred', issuer: 'Chase', creditAmount: 0 },
      { name: 'Capital One Venture X', issuer: 'Capital One', creditAmount: 0 },
    ];
  }, [refData.cardPerks]);

  // Handle local allocation change (doesn't save immediately)
  const handleLocalAllocationChange = (key, value) => {
    setLocalAllocations(prev => ({
      ...prev,
      [key]: parseFloat(value) || 0
    }));
    setHasUnsavedChanges(true);
  };

  // Confirm and save all changes
  const handleConfirmChanges = async (cardId) => {
    setIsSaving(true);
    
    try {
      const card = creditCards.find(c => c.id === cardId);
      if (!card) return;
      
      // Capture current values from DOM inputs (in case onBlur didn't fire)
      const finalAllocations = { ...localAllocations };
      
      // Get all input values directly from DOM
      const inputs = document.querySelectorAll('[data-allocation-key]');
      inputs.forEach(input => {
        const key = input.getAttribute('data-allocation-key');
        if (key) {
          finalAllocations[key] = parseFloat(input.value) || 0;
        }
      });
      
      // Save ALL bundle allocations and update payment_method
      for (const bundle of allBundlesForAllocation) {
        const key = `bundle-${bundle.id}`;
        const newValue = finalAllocations[key] || 0;
        
        // FIXED: Pass cardId to saveBundleAllocation
        // This ensures the credit_card_allocations record is created
        await saveBundleAllocation(bundle.id, newValue, cardId);
        
        // Update payment_method based on credit value
        if (newValue > 0) {
          // Has credit - set to credit_card_perk with payment_source
          await supabase
            .from('user_bundles')
            .update({
              payment_method: 'credit_card_perk',
              payment_source: card.card_name,
            })
            .eq('id', bundle.id);
        } else if (bundle.payment_method === 'credit_card_perk' && bundle.payment_source === card.card_name) {
          // Had credit from this card but now 0 - reset to paid
          await supabase
            .from('user_bundles')
            .update({
              payment_method: 'paid',
              payment_source: null,
            })
            .eq('id', bundle.id);
        }
      }
      
      // Save ALL service allocations AND update payment_method
      for (const sub of servicesForAllocation) {
        const key = `service-${sub.id}`;
        const newValue = finalAllocations[key] || 0;
        
        // Save the allocation
        await saveAllocation(cardId, sub.id, newValue);
        
        // Update payment_method based on credit value
        if (newValue > 0) {
          // Has credit - set to credit_card_perk with payment_source
          await supabase
            .from('subscriptions')
            .update({
              payment_method: 'credit_card_perk',
              payment_source: card.card_name,
            })
            .eq('id', sub.id);
        } else if (sub.payment_method === 'credit_card_perk' || sub.payment_method === 'card_perk') {
          // Had credit but now 0 - reset to paid
          await supabase
            .from('subscriptions')
            .update({
              payment_method: 'paid',
              payment_source: null,
            })
            .eq('id', sub.id);
        }
      }
      
      setHasUnsavedChanges(false);
      setExpandedCardId(null); // Close panel after saving
      
      // Reload profile to refresh all calculated values (waterfall, etc.)
      await loadProfile();
      
      toast.success('Credit allocations saved!');
      
    } catch (error) {
      console.error('Error saving allocations:', error);
      toast.error('Failed to save allocations');
    } finally {
      setIsSaving(false);
    }
  };

  // Cancel and reset to database values
  const handleCancelChanges = () => {
    setLocalAllocations({});
    setHasUnsavedChanges(false);
    setExpandedCardId(null);
    setShowUnsavedModal(false);
    setPendingCloseCardId(null);
  };

  // Handle attempt to close panel
  const handleCloseAttempt = (cardId) => {
    if (hasUnsavedChanges) {
      setPendingCloseCardId(cardId);
      setShowUnsavedModal(true);
    } else {
      setExpandedCardId(null);
    }
  };

  // Handle confirm from unsaved modal
  const handleConfirmFromModal = async () => {
    if (pendingCloseCardId) {
      await handleConfirmChanges(pendingCloseCardId);
    }
    setShowUnsavedModal(false);
    setPendingCloseCardId(null);
  };

  // Handle discard from unsaved modal
  const handleDiscardFromModal = () => {
    handleCancelChanges();
  };

  const handleCardSelect = (e) => {
    const selectedName = e.target.value;

    if (selectedName === 'Other') {
      setNewCard({
        card_name: '',
        issuer: '',
        streaming_credit_monthly: 0,
        isOther: true,
      });
    } else {
      const selected = cardOptions.find(c => c.name === selectedName);
      setNewCard({
        card_name: selectedName,
        issuer: selected?.issuer || '',
        streaming_credit_monthly: selected?.creditAmount || 0,
        isOther: false,
      });
    }
  };

  const handleAddCard = async () => {
    if (!newCard.card_name) return;

    const card = addCreditCard({
      card_name: newCard.card_name,
      issuer: newCard.issuer,
      streaming_credit_monthly: newCard.streaming_credit_monthly || 0,
    });

    await saveCreditCard(card);

    setNewCard({
      card_name: '',
      issuer: '',
      streaming_credit_monthly: 0,
      isOther: false,
    });
    setShowAddForm(false);
  };

  const handleRemoveCard = async (cardId) => {
    if (window.confirm('Remove this card? Any credit allocations will be deleted.')) {
      await deleteCreditCard(cardId);
    }
  };

  const summary = creditCards.length > 0
    ? `${creditCards.length} card${creditCards.length > 1 ? 's' : ''} • $${creditCards.reduce((sum, c) => sum + (c.streaming_credit_monthly || 0), 0).toFixed(2)}/mo credits`
    : 'No cards added';

  // Calculate totals from local state for display
  const calculateLocalTotals = (cardId, cardName) => {
    let bundleTotal = 0;
    allBundlesForAllocation.forEach(bundle => {
      const key = `bundle-${bundle.id}`;
      bundleTotal += parseFloat(localAllocations[key]) || 0;
    });
    
    let serviceTotal = 0;
    servicesForAllocation.forEach(sub => {
      const key = `service-${sub.id}`;
      serviceTotal += parseFloat(localAllocations[key]) || 0;
    });
    
    return bundleTotal + serviceTotal;
  };

  return (
    <SectionWrapper
      title="Credit Cards"
      icon={CreditCard}
      isExpanded={isExpanded}
      onToggle={() => toggleSection('creditCards')}
      isDirty={isDirty}
      isSaving={saving}
      summary={summary}
      badge={creditCards.length > 0 ? creditCards.length : null}
    >
      <div className="space-y-4">
        {/* Existing Cards */}
        {creditCards.map(card => {
          const allocations = getCardAllocations(card.id);
          const cardBundles = getBundlesForCard(card.card_name);
          
          // Calculate total allocated from database (for collapsed view)
          const dbServiceAllocated = allocations.reduce((sum, a) => sum + parseFloat(a.amount_allocated || 0), 0);
          // For bundles, only count those currently assigned to this card
          const dbBundleAllocated = allBundlesForAllocation
            .filter(b => b.payment_source === card.card_name)
            .reduce((sum, b) => sum + (parseFloat(b.credit_applied) || 0), 0);
          const dbTotalAllocated = dbServiceAllocated + dbBundleAllocated;
          
          // For expanded view, use local state
          const isCardExpanded = expandedCardId === card.id;
          const displayAllocated = isCardExpanded 
            ? calculateLocalTotals(card.id, card.card_name)
            : dbTotalAllocated;
          
          const remaining = (card.streaming_credit_monthly || 0) - displayAllocated;

          return (
            <div key={card.id} className="border border-gray-200 rounded-lg overflow-hidden">
              {/* Card Header */}
              <div className="p-4 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{card.card_name}</div>
                    {card.issuer && <div className="text-sm text-gray-500">{card.issuer}</div>}
                    {card.streaming_credit_monthly > 0 && (
                      <div className="text-sm text-green-600 mt-1">
                        ${card.streaming_credit_monthly}/mo streaming credit
                        {!isCardExpanded && remaining > 0.01 && (
                          <span className="text-yellow-600 ml-2">(${remaining.toFixed(2)} unused)</span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    {isCardExpanded && (
                      <button
                        onClick={() => handleCloseAttempt(card.id)}
                        className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                        title="Close"
                      >
                        <ChevronUp className="w-5 h-5" />
                      </button>
                    )}
                    <button
                      onClick={() => handleRemoveCard(card.id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Remove card"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                {/* Assign Credits Button - only show when collapsed */}
                {!isCardExpanded && card.streaming_credit_monthly > 0 && (
                  <button
                    onClick={() => setExpandedCardId(card.id)}
                    className="mt-3 w-full px-4 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 font-medium rounded-lg transition-colors text-sm"
                  >
                    {dbTotalAllocated > 0 ? 'Reassign Credits' : 'Assign Credits'}
                  </button>
                )}
              </div>

              {/* Credit Allocation (Expanded) */}
              {isCardExpanded && card.streaming_credit_monthly > 0 && (
                <div className="p-4 border-t border-gray-200 bg-white">
                  <div className="flex items-center gap-2 mb-4">
                    <DollarSign className="w-4 h-4 text-purple-600" />
                    <h4 className="font-medium text-gray-900">Allocate Credits</h4>
                  </div>

                  {/* Credit Summary */}
                  <div className="grid grid-cols-3 gap-3 mb-4 p-3 bg-purple-50 rounded-lg">
                    <div className="text-center">
                      <p className="text-lg font-bold text-gray-900">${card.streaming_credit_monthly.toFixed(2)}</p>
                      <p className="text-xs text-gray-500">Available</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-gray-900">${displayAllocated.toFixed(2)}</p>
                      <p className="text-xs text-gray-500">Allocated</p>
                    </div>
                    <div className="text-center">
                      <p className={`text-lg font-bold ${remaining < -0.01 ? 'text-red-600' : remaining > 0.01 ? 'text-yellow-600' : 'text-green-600'}`}>
                        ${remaining.toFixed(2)}
                      </p>
                      <p className="text-xs text-gray-500">Remaining</p>
                    </div>
                  </div>

                  {remaining < -0.01 && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-800">
                        You've allocated more than available! Reduce allocations by ${Math.abs(remaining).toFixed(2)}.
                      </p>
                    </div>
                  )}

                  {/* Allocation Inputs */}
                  <div className="space-y-3">
                    
                    {/* BUNDLES SECTION - Show ALL bundles */}
                    {allBundlesForAllocation.length > 0 && (
                      <>
                        <div className="flex items-center gap-2 text-sm text-gray-500 mt-2">
                          <Package className="w-4 h-4" />
                          <span className="font-medium">Bundles</span>
                        </div>
                        {allBundlesForAllocation.map(bundle => {
                          const bundleRef = refData?.bundles?.find(b => b.bundle_code === bundle.bundle_code);
                          const bundleName = bundleRef?.bundle_name || bundle.bundle_code;
                          const key = `bundle-${bundle.id}`;
                          const currentAmount = localAllocations[key] ?? parseFloat(bundle.credit_applied) ?? 0;

                          return (
                            <div key={bundle.id} className="flex items-center gap-3 pl-6 py-2 bg-purple-50 rounded-lg">
                              <div className="flex-1">
                                <p className="text-sm font-medium text-gray-700">
                                  {bundleName}
                                </p>
                                <p className="text-xs text-gray-500">${bundle.monthly_cost}/mo</p>
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="text-sm text-gray-500">$</span>
                                <input
                                  key={`${key}-${currentAmount}`}
                                  data-allocation-key={key}
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  max={bundle.monthly_cost}
                                  defaultValue={currentAmount || ''}
                                  onBlur={(e) => {
                                    handleLocalAllocationChange(key, e.target.value);
                                  }}
                                  className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
                                  placeholder="0.00"
                                />
                              </div>
                            </div>
                          );
                        })}
                      </>
                    )}
                    
                    {/* SERVICES SECTION */}
                    {servicesForAllocation.length > 0 && (
                      <>
                        <div className="flex items-center gap-2 text-sm text-gray-500 mt-4">
                          <CreditCard className="w-4 h-4" />
                          <span className="font-medium">Services</span>
                        </div>
                        {servicesForAllocation.map(sub => {
                          const key = `service-${sub.id}`;
                          const allocation = allocations.find(a => a.subscription_id === sub.id);
                          const dbAmount = parseFloat(allocation?.amount_allocated) || 0;
                          const currentAmount = localAllocations[key] ?? dbAmount;

                          return (
                            <div key={sub.id} className="flex items-center gap-3">
                              <div className="flex-1">
                                <p className="text-sm font-medium text-gray-700">
                                  {sub.service_name}
                                  {sub.subscription_tier && (
                                    <span className="text-gray-400 ml-1">({sub.subscription_tier})</span>
                                  )}
                                </p>
                                {sub.monthly_cost > 0 && (
                                  <p className="text-xs text-gray-500">${sub.monthly_cost}/mo</p>
                                )}
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="text-sm text-gray-500">$</span>
                                <input
                                  key={`${key}-${currentAmount}`}
                                  data-allocation-key={key}
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  defaultValue={currentAmount || ''}
                                  onBlur={(e) => {
                                    handleLocalAllocationChange(key, e.target.value);
                                  }}
                                  className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
                                  placeholder="0.00"
                                />
                              </div>
                            </div>
                          );
                        })}
                      </>
                    )}
                    
                    {allBundlesForAllocation.length === 0 && servicesForAllocation.length === 0 && (
                      <p className="text-sm text-gray-500 text-center py-4">
                        Add subscriptions or bundles first to allocate credits
                      </p>
                    )}
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="mt-6 pt-4 border-t border-gray-200 flex gap-3">
                    <button
                      onClick={handleCancelChanges}
                      disabled={isSaving}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors bg-gray-100 hover:bg-gray-200 text-gray-700"
                    >
                      <X className="w-5 h-5" />
                      Cancel
                    </button>
                    <button
                      onClick={() => handleConfirmChanges(card.id)}
                      disabled={!hasUnsavedChanges || isSaving || remaining < -0.01}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors ${
                        hasUnsavedChanges && remaining >= -0.01
                          ? 'bg-green-600 hover:bg-green-700 text-white'
                          : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {isSaving ? (
                        <>
                          <span className="animate-spin">⏳</span>
                          Saving...
                        </>
                      ) : (
                        <>
                          <Check className="w-5 h-5" />
                          {hasUnsavedChanges ? 'Confirm' : 'No Changes'}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Empty State */}
        {creditCards.length === 0 && !showAddForm && (
          <div className="text-center py-6 text-gray-500">
            <CreditCard className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-sm">No credit cards added yet</p>
            <p className="text-xs mt-1">Add cards with streaming credits to maximize savings</p>
          </div>
        )}

        {/* Add Card Form */}
        {showAddForm && (
          <div className="border-2 border-purple-200 rounded-lg p-4 space-y-4">
            <h4 className="font-medium text-gray-900">Add Credit Card</h4>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Card
              </label>
              <select
                value={newCard.isOther ? 'Other' : newCard.card_name}
                onChange={handleCardSelect}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="">Choose a card...</option>
                {cardOptions.map(card => (
                  <option key={card.name} value={card.name}>
                    {card.name}
                    {card.creditAmount > 0 && ` ($${card.creditAmount}/mo credit)`}
                  </option>
                ))}
                <option value="Other">Other</option>
              </select>
            </div>

            {newCard.isOther && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Card Name
                  </label>
                  <input
                    type="text"
                    value={newCard.card_name}
                    onChange={(e) => setNewCard({ ...newCard, card_name: e.target.value })}
                    placeholder="Enter card name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Issuer
                  </label>
                  <input
                    type="text"
                    value={newCard.issuer}
                    onChange={(e) => setNewCard({ ...newCard, issuer: e.target.value })}
                    placeholder="e.g., Chase, Amex"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Monthly Streaming Credit
              </label>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newCard.streaming_credit_monthly || ''}
                  onChange={(e) => setNewCard({ ...newCard, streaming_credit_monthly: parseFloat(e.target.value) || 0 })}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="0.00"
                />
                <span className="text-sm text-gray-500">/month</span>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowAddForm(false)}
                className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddCard}
                disabled={!newCard.card_name}
                className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 text-white font-medium rounded-lg transition-colors"
              >
                Add Card
              </button>
            </div>
          </div>
        )}

        {/* Add Button - only show when no card is expanded */}
        {!showAddForm && !expandedCardId && (
          <button
            onClick={() => setShowAddForm(true)}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 hover:border-purple-400 text-gray-600 hover:text-purple-600 rounded-lg transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add Credit Card
          </button>
        )}

        {/* Unsaved Changes Modal */}
        {showUnsavedModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-sm mx-4 shadow-xl">
              <div className="flex items-center gap-3 mb-4">
                <AlertCircle className="w-6 h-6 text-yellow-500" />
                <h3 className="text-lg font-semibold text-gray-900">Unsaved Changes</h3>
              </div>
              <p className="text-gray-600 mb-6">
                You have unsaved changes to your credit allocations. Would you like to save them?
              </p>
              <div className="flex gap-3">
                <button
                  onClick={handleDiscardFromModal}
                  className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
                >
                  Discard
                </button>
                <button
                  onClick={handleConfirmFromModal}
                  className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </SectionWrapper>
  );
};

export default CreditCardsSection;